// @givmo/design-system — Button.
//
// LEGACY path (ports Flutter `BaseRoundButton` + `BaseRectangleButton`), kept INTACT so the
// `donate` app + the existing tests keep passing:
//   - `round`     → BaseRoundButton: full-width pill (radius all50), primary blue,
//                   pressed-state highlight #1F55D7 (base_round_button.dart:54), optional
//                   trailing chevron, loading spinner, disabled → gray20 bg / gray60 label.
//   - `rectangle` → BaseRectangleButton: rounded-rect (radius all15), otherwise the same
//                   active/disabled + optional chevron behavior.
// Label typography matches Flutter `h5TextStyleSecondary` (Lato w600 s20).
//
// SaaS path (added 2026-06-26, ADDITIVE): pass `appearance="saas"` to opt into the fintech-grade
// button used by the rebuilt admin/super-admin portals — `rounded-md`, sized `sm`/`md`/`lg`
// (32/36/40px), tones `primary`/`secondary`(outline)/`ghost`/`danger`/`link`, optional
// leading/trailing icons, a loading spinner, and NOT full-width (an inline-flex auto-width
// control). The legacy default (`appearance="legacy"`) is unchanged.
//
// `disabled` mirrors Flutter's `isActive=false`. `loading` suppresses the click and swaps the
// label for a spinner.
import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { FOCUS_RING } from '../lib/focus';
import { testIdProp, type TestIdProps } from '../lib/testid';
import { Icon } from './icon';

// ── Legacy (Flutter-ported) variants ──────────────────────────────────────────────────────────
const buttonVariants = cva(
  // Base: flex row, centered label, Lato w600 s20 (h5TextStyleSecondary), the house keyboard focus,
  // pointer cursor, disabled handling.
  [
    'inline-flex w-full items-center justify-center gap-8',
    'font-sans text-s20 font-w600 tracking-givmo',
    'select-none transition-colors outline-none',
    // Focus = the house pairing, {@link FOCUS_RING}, exactly as the SaaS recipe below wears it.
    // `outline-none` drops the UA ring and, until EP-WEB45, nothing put one back: a keyboard user
    // reaching a legacy Button (pay's "Continue to payment", super-admin's legacy dialogs) saw no
    // focus at all. An app that styles `button:focus-visible` itself (donate's app.css, embed's CTA
    // rule) keeps its own outline, since an unlayered rule outranks these utilities; there the
    // ring's tint is all this adds. `:focus-visible` only, so a mouse press shows nothing new.
    FOCUS_RING,
    'disabled:cursor-not-allowed',
  ],
  {
    variants: {
      variant: {
        round: 'rounded-all50 px-25 py-14',
        rectangle: 'rounded-all15 px-24 py-14',
      },
      tone: {
        // Active primary; pressed highlight = #1F55D7 (color-pressed). The label is `on-brand`
        // (white in both schemes), NOT `white`: a partner's dark Appearance flips `--color-white`
        // to the dark surface while the brand fill stays, which painted this label dark-on-blue.
        primary: 'bg-blue text-on-brand active:bg-pressed',
      },
    },
    compoundVariants: [
      {
        // Disabled look is shared across variants (Flutter isActive=false).
        tone: 'primary',
        className: 'disabled:bg-gray20 disabled:text-gray60 disabled:active:bg-gray20',
      },
    ],
    defaultVariants: {
      variant: 'round',
      tone: 'primary',
    },
  },
);

// ── SaaS variants (additive) ──────────────────────────────────────────────────────────────────
const saasButtonVariants = cva(
  [
    // `relative`: the loading spinner is positioned over the (invisible) label, so a working
    // button keeps the width its label gave it — see the `loading` branch below.
    'relative inline-flex items-center justify-center gap-6 whitespace-nowrap',
    'font-sans font-w600 tracking-givmo rounded-md',
    'select-none transition-[background-color,box-shadow,border-color,color] duration-150 outline-none',
    // Focus = tinted ring AND a 2px solid outline — the house pairing, published once as
    // {@link FOCUS_RING} (which carries the contrast arithmetic and why `outline-solid` is
    // load-bearing). The `link` tone below and the `danger` compound variant deliberately override
    // parts of it; both still resolve against this base through `cn`, exactly as before.
    FOCUS_RING,
    'disabled:cursor-not-allowed',
  ],
  {
    variants: {
      size: {
        sm: 'h-32 px-12 text-s13',
        md: 'h-36 px-14 text-s14',
        lg: 'h-[40px] px-16 text-s14',
      },
      tone: {
        // Filled tones share ONE disabled look: a flat neutral chip (NOT a faded primary, which
        // reads as loading) — neutral-100 bg / neutral-400 text, no shadow/border.
        primary:
          'bg-brand-600 text-on-brand shadow-xs hover:bg-brand-700 active:bg-brand-800 disabled:bg-neutral-100 disabled:text-neutral-400 disabled:shadow-none',
        // secondary (outline): hover lifts the border to neutral-400, active deepens to neutral-150
        // (a touch darker than ghost's active, so the two read as distinct affordances).
        secondary:
          'border border-border-strong bg-surface text-text-strong shadow-xs hover:border-neutral-400 hover:bg-surface-hover active:bg-neutral-150 disabled:border-border-subtle disabled:bg-neutral-50 disabled:text-neutral-400 disabled:shadow-none',
        // ghost: no border/fill at rest; active is the lighter neutral-100.
        ghost:
          'text-text-secondary hover:bg-surface-hover active:bg-neutral-100 disabled:text-neutral-400',
        danger:
          'bg-danger-600 text-white shadow-xs hover:bg-danger-700 active:bg-danger-700 disabled:bg-neutral-100 disabled:text-neutral-400 disabled:shadow-none',
        // link: zero-padding inline text — focus shows an UNDERLINE, not a detached floating ring
        // or outline. The underline is its own ≥3:1 indicator (brand-600 on white = 6.56:1).
        link: 'h-auto px-0 text-brand-600 hover:text-brand-700 hover:underline underline-offset-2 focus-visible:shadow-none focus-visible:outline-none focus-visible:underline disabled:text-neutral-400 disabled:no-underline',
      },
    },
    compoundVariants: [
      // `danger` gets the danger focus ring + outline rather than the brand pair
      // (danger-600 = 4.83:1 on white, 3.92:1 against its own ring tint).
      {
        tone: 'danger',
        className: 'focus-visible:shadow-ring-danger focus-visible:outline-danger-600',
      },
      // `link` ignores size height; keep its hit area tight.
      { tone: 'link', size: 'sm', className: 'h-auto px-0 text-s13' },
      { tone: 'link', size: 'md', className: 'h-auto px-0 text-s14' },
      { tone: 'link', size: 'lg', className: 'h-auto px-0 text-s14' },
    ],
    defaultVariants: {
      size: 'md',
      tone: 'primary',
    },
  },
);

export type SaasButtonTone = NonNullable<VariantProps<typeof saasButtonVariants>['tone']>;
export type SaasButtonSize = NonNullable<VariantProps<typeof saasButtonVariants>['size']>;

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'>, TestIdProps {
  /**
   * `legacy` (default) → the Flutter-ported `round`/`rectangle` button (unchanged).
   * `saas`             → the fintech-grade admin button (size/tone/icons, not full-width).
   */
  appearance?: 'legacy' | 'saas';
  // Legacy props (only meaningful when appearance === 'legacy').
  variant?: 'round' | 'rectangle';
  // Shared name across both APIs; the SaaS path accepts the wider tone set.
  tone?: 'primary' | SaasButtonTone;
  // SaaS-only props.
  size?: SaasButtonSize;
  /** Leading icon (Lucide component) — SaaS appearance only. */
  leadingIcon?: ReactNode;
  /** Trailing icon (Lucide component) — SaaS appearance only. */
  trailingIcon?: ReactNode;
  /** Stretch to fill the container (SaaS appearance is auto-width by default). */
  fullWidth?: boolean;
  /** Render a trailing chevron (Flutter `withIcon`) — legacy appearance only. */
  withIcon?: boolean;
  /** Show a spinner in place of the label and suppress clicks (Flutter `isLoading`). */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    appearance = 'legacy',
    variant,
    tone,
    size,
    className,
    children,
    leadingIcon,
    trailingIcon,
    fullWidth = false,
    withIcon = false,
    loading = false,
    disabled = false,
    testId,
    onClick,
    type = 'button',
    ...props
  },
  ref,
) {
  if (appearance === 'saas') {
    const saasTone = (tone ?? 'primary') as SaasButtonTone;
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={cn(
          saasButtonVariants({ size, tone: saasTone }),
          fullWidth && saasTone !== 'link' && 'w-full',
          className,
        )}
        onClick={loading ? undefined : onClick}
        {...testIdProp(testId)}
        {...props}
      >
        {loading ? (
          <>
            {/* ⚠ THE LABEL STAYS IN FLOW, INVISIBLE, so the control keeps its width while it works.
                Swapping the label for a bare spinner collapsed a footer's destructive button to a
                chip mid-act (the OM-9 confirm dialog, seen at its first capture), and every sibling
                control shifted with it. The spinner sits over the hidden label instead. */}
            <span aria-hidden className="invisible flex items-center gap-6">
              {leadingIcon && <span className="flex shrink-0">{leadingIcon}</span>}
              {children != null && children !== '' && <span>{children}</span>}
              {trailingIcon && <span className="flex shrink-0">{trailingIcon}</span>}
            </span>
            <span
              role="status"
              aria-label="Loading"
              className={cn(
                'absolute inset-0 m-auto animate-spin rounded-full border-2 border-current border-t-transparent',
                // Sized by CLASS, never an inline style: a nonce-only `style-src` (donate, pay,
                // consent) refuses a server-rendered style attribute, and this spinner is the
                // one saas element that carried one.
                size === 'sm' ? 'size-[14px]' : 'size-[16px]',
              )}
            />
          </>
        ) : (
          <>
            {leadingIcon && <span className="flex shrink-0">{leadingIcon}</span>}
            {children != null && children !== '' && <span>{children}</span>}
            {trailingIcon && <span className="flex shrink-0">{trailingIcon}</span>}
          </>
        )}
      </button>
    );
  }

  // ── Legacy path (unchanged behavior) ──
  const legacyTone = (tone ?? 'primary') as 'primary';
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, tone: legacyTone }), className)}
      onClick={loading ? undefined : onClick}
      {...testIdProp(testId)}
      {...props}
    >
      {loading ? (
        <span
          role="status"
          aria-label="Loading"
          className="size-[29px] animate-spin rounded-all100 border-2 border-current border-t-transparent"
        />
      ) : (
        <>
          <span>{children}</span>
          {withIcon && <Icon icon={ChevronRight} size={variant === 'rectangle' ? 16 : 24} />}
        </>
      )}
    </button>
  );
});

export { buttonVariants, saasButtonVariants };
